// Modules visibles du niveau 250 (arène libre seulement ; choix de Léni, 5 novembre 2026).
// Quatre emplacements, aux niveaux 100, 150, 200 et 250, à choisir parmi six modules, chacun une seule fois.
// Chaque module se voit sur le tank pour tous les joueurs (bit transmis dans l'instantané). Un module réutilise
// les effets des modifications (bouclier, cumul de multiplicateurs plafonnés, aides de détection) ; tourelle et
// drones ajoutent un tube automatique à l'arme effective (server/src/game/loadout.ts).
import type { Fx, Mod } from './mods';

export const MODULE_LEVELS = [100, 150, 200, 250] as const;

export type ModuleId = 'MD-SHD' | 'MD-TUR' | 'MD-DRN' | 'MD-ARM' | 'MD-THR' | 'MD-RAD';

export interface ModuleDef {
  id: ModuleId;
  /** Bit du module dans l'instantané (rendu chez tous les joueurs). */
  bit: number;
  name: string;
  desc: string;
  mods: Mod[];
  fx: Fx[];
}

export const MODULES: ModuleDef[] = [
  {
    id: 'MD-SHD',
    bit: 1,
    name: 'Bouclier',
    desc: "anneau d'énergie qui absorbe 30 % de la vie max, rechargé 5 s après le dernier coup ; remplace le bouclier d'une modification",
    mods: [],
    fx: [{ t: 'shield', pct: 0.3, delay: 5, rate: 0.25, visible: true }],
  },
  {
    id: 'MD-TUR',
    bit: 2,
    name: 'Tourelle auto',
    desc: 'petite tourelle sur la caisse qui vise seule la cible la plus proche (2 tirs par seconde, 60 % des dégâts d’un tir)',
    mods: [],
    fx: [{ t: 'modTurret' }],
  },
  {
    id: 'MD-DRN',
    bit: 4,
    name: "Drones d'escorte",
    desc: "deux drones tournent autour du tank et attaquent seuls ce qui s'approche à moins de 420 unités",
    mods: [],
    fx: [{ t: 'modDrones', count: 2 }],
  },
  {
    id: 'MD-ARM',
    bit: 8,
    name: 'Blindage',
    desc: 'plaques de blindage : −18 % de dégâts reçus, −5 % de vitesse',
    mods: [
      { k: 'taken', v: 0.82 },
      { k: 'move', v: 0.95 },
    ],
    fx: [],
  },
  {
    id: 'MD-THR',
    bit: 16,
    name: 'Propulseurs',
    desc: "propulseurs arrière : +12 % de vitesse, accélération +30 %",
    mods: [
      { k: 'move', v: 1.12 },
      { k: 'resp', v: 1.3 },
    ],
    fx: [],
  },
  {
    id: 'MD-RAD',
    bit: 32,
    name: 'Radar',
    desc: 'antenne radar : flèches vers les menaces visibles proches, le boss et le dernier agresseur (aucune vision en plus)',
    mods: [],
    fx: [
      { t: 'info', what: 'radar' },
      { t: 'info', what: 'bossCompass' },
      { t: 'info', what: 'aggressor' },
    ],
  },
];

export const MODULE_DEFS: Record<string, ModuleDef> = Object.fromEntries(MODULES.map((m) => [m.id, m]));

/** Nombre d'emplacements de module ouverts à ce niveau (0 jusqu'au niveau 99). */
export function moduleSlotsAt(level: number): number {
  return MODULE_LEVELS.filter((l) => level >= l).length;
}

/** Un module peut-il être ajouté ? Emplacement libre, module connu, jamais deux fois le même. */
export function canEquipModule(level: number, equipped: string[], id: string): boolean {
  return !!MODULE_DEFS[id] && !equipped.includes(id) && equipped.length < moduleSlotsAt(level);
}

/** Masque des modules (bits) pour l'instantané. */
export function moduleMask(ids: string[]): number {
  let m = 0;
  for (const id of ids) m |= MODULE_DEFS[id]?.bit ?? 0;
  return m;
}
